// import { adjustToUserTimezone } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, BoxProps } from '@mui/material';
import { parseISO } from 'date-fns';
import { useEffect } from 'react';
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

export interface IFormContainerProps {
  children: React.ReactNode;
  onSubmit: (data: any) => void;
  resolver: z.ZodObject<any> | z.ZodEffects<any>;
  defaultValues?: Record<string, any>;
  watchDefaultValues?: boolean;
  sx?: BoxProps['sx'];
  onError?: SubmitErrorHandler<Record<string, any>>;
  stopPropagation?: boolean;
}

export function sanitizeValues(values: Record<string, any>): any {
  const dateRegex = new RegExp(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}.\d{3}Z$/);

  if (values === null || values === undefined) return '';
  if (values instanceof Date) return values;
  if (dateRegex.test(values as any)) return parseISO(values as any);
  if (typeof values === 'boolean') return values;
  if (typeof values !== 'object') return String(values);

  if (Array.isArray(values)) {
    if (values.length === 0) return [];
    return values.map(sanitizeValues);
  }

  return Object.keys(values).reduce((acc, key) => {
    // @ts-expect-error
    acc[key] = sanitizeValues(values[key]);
    return acc;
  }, {});
}

/** Rola até o primeiro campo com erro. A mensagem fica no próprio campo. */
export function scrollToFirstError(form: HTMLElement | null) {
  setTimeout(() => {
    const root = form ?? document;
    const invalid = root.querySelector<HTMLElement>('[aria-invalid="true"], .Mui-error');
    if (!invalid) return;

    invalid.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const input = invalid.matches('input, textarea, select')
      ? invalid
      : invalid.querySelector<HTMLElement>('input, textarea, select');
    input?.focus({ preventScroll: true });
  }, 0);
}

export function FormContainerRHF({
  children,
  onSubmit,
  resolver,
  defaultValues,
  watchDefaultValues,
  sx,
  onError,
  stopPropagation
}: IFormContainerProps) {
  const methods = useForm({
    resolver: resolver ? zodResolver(resolver) : undefined,
    defaultValues
  });

  useEffect(() => {
    if (watchDefaultValues) return;
    if (!defaultValues) return;

    methods.reset({ ...methods.getValues(), ...sanitizeValues(defaultValues) });
  }, []);

  useEffect(() => {
    if (!watchDefaultValues) return;
    if (!defaultValues) return;

    methods.reset({ ...methods.getValues(), ...sanitizeValues(defaultValues) });
  }, [defaultValues]);

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={(e) => {
          if (stopPropagation) e.stopPropagation();
          const form = e.currentTarget as HTMLElement;
          methods.handleSubmit(onSubmit, (errors, event) => {
            scrollToFirstError(form);
            onError?.(errors, event);
          })(e);
        }}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          ...sx
        }}
      >
        {children}
      </Box>
    </FormProvider>
  );
}
